import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Switch,
  Table,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  configOptionApi,
  ConfigOption,
} from "../../services/configOption.api";
import { getMsg, getModal } from "../../utils/antdStatic";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

/** Normaliza para búsqueda: minúsculas, sin tildes */
const strip = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const CATEGORIAS = [
  { key: "banco", label: "Bancos" },
  { key: "tipocuenta", label: "Tipos de cuenta" },
  { key: "plan", label: "Planes de propiedad" },
];

function CategorySection({
  categoria,
  label,
  clavePersistencia,
}: {
  categoria: string;
  label: string;
  /**
   * Clave con la que esta sección recuerda su buscador. La pone quien la usa:
   * hay una sección por categoría en la misma pantalla y una clave inventada
   * adentro las colapsaría en un solo filtro compartido.
   */
  clavePersistencia: string;
}) {
  const [options, setOptions] = useState<ConfigOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigOption | null>(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useFiltroPersistido(`${clavePersistencia}.busqueda`, "", {
    validar: (v) => typeof v === "string",
  });

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    const res = await configOptionApi.listAll(categoria);
    if (res?.data) setOptions(res.data);
    setLoading(false);
  }, [categoria]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleSave = async () => {
    const values = await form.validateFields();
    const result = editing
      ? await configOptionApi.update(editing.id, values)
      : await configOptionApi.create({ categoria, ...values });
    // apiFetch retorna null en cualquier error (409 duplicado, 500, red…)
    // y ya muestra el toast. Dejamos el modal abierto para que el usuario
    // corrija el valor sin perder lo que tipeó.
    if (!result) {
      fetchOptions();
      return;
    }
    // Al renombrar un banco / tipo de cuenta, el backend propaga el nombre
    // nuevo a las fichas que tenían el viejo. Informamos el impacto para que
    // el admin sepa que no quedaron datos desincronizados. (`propagated` solo
    // viene en la respuesta de update; en create es undefined → 0.)
    const propagated = (result as { propagated?: number }).propagated ?? 0;
    if (editing && propagated > 0) {
      getMsg()?.info(
        `${propagated} ficha${propagated === 1 ? "" : "s"} actualizada${propagated === 1 ? "" : "s"} con el nuevo nombre`,
      );
    }
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    fetchOptions();
  };

  const handleToggleActivo = async (record: ConfigOption) => {
    await configOptionApi.update(record.id, { activo: !record.activo });
    fetchOptions();
  };

  // Eliminación definitiva: primero consulta cuántas entidades usan este
  // valor. `plan` cruza contra properties.plan; `banco`/`tipocuenta` cruzan
  // contra los datos bancarios de los clientes (users). El mensaje y el
  // efecto difieren según la entidad afectada (ver más abajo).
  const handleHardDelete = async (record: ConfigOption) => {
    const usage = await configOptionApi.usage(record.id);
    const count = usage?.count ?? 0;
    const entity = usage?.entity ?? null; // "propiedad" | "cliente" | null

    // `Modal.confirm` (import estático de antd) no funciona en AntD v5 + React 19
    // porque crea un root huérfano — por eso existe antdStatic. Usamos la
    // instancia capturada de <App.useApp()> que sí se monta en el árbol.
    const modal = getModal();
    if (!modal) {
      getMsg()?.error("La interfaz no está lista todavía, vuelve a intentar.");
      return;
    }

    // Mensaje según a qué entidad afecta el valor:
    //  - plan (propiedades): al borrar, la propiedad queda SIN plan (set
    //    NULL) y hay que reasignar manualmente.
    //  - banco / tipo de cuenta (clientes): el dato bancario se PRESERVA en
    //    la ficha del cliente; solo deja de poder seleccionarse de nuevo.
    let content: string;
    if (count > 0 && entity === "propiedad") {
      content = `${count} propiedad${count === 1 ? "" : "es"} usa${count === 1 ? "" : "n"} este plan. Tras eliminar, ${count === 1 ? "quedará" : "quedarán"} sin plan asignado y habrá que reasignar manualmente. ¿Confirmas?`;
    } else if (count > 0 && entity === "cliente") {
      content = `${count} cliente${count === 1 ? "" : "s"} tiene${count === 1 ? "" : "n"} este valor guardado. Tras eliminar, seguirá guardado en su ficha pero no podrás volver a seleccionarlo del desplegable. ¿Confirmas?`;
    } else {
      content = `Vas a eliminar "${record.valor}" definitivamente. No se puede deshacer.`;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: `Eliminar "${record.valor}"`,
        content,
        okText: "Eliminar",
        okButtonProps: { danger: true },
        cancelText: "Cancelar",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    const result = await configOptionApi.hardDelete(record.id);
    if (result) {
      // Solo `plan` limpia referencias (properties.plan = NULL); para
      // banco/tipocuenta clearedProperties siempre es 0 (datos preservados).
      if (result.clearedProperties > 0) {
        getMsg()?.info(
          `${result.clearedProperties} propiedad${result.clearedProperties === 1 ? "" : "es"} quedó sin plan`,
        );
      }
      fetchOptions();
    }
  };

  const filteredOptions = useMemo(() => {
    const needle = strip(search);
    if (!needle) return options;
    return options.filter((o) => strip(o.valor).includes(needle));
  }, [options, search]);

  const showDescripcion = categoria === "plan";

  const columns = [
    { title: "Valor", dataIndex: "valor", key: "valor" },
    ...(showDescripcion
      ? [{ title: "Descripción", dataIndex: "descripcion", key: "descripcion", ellipsis: true }]
      : []),
    {
      title: "Activo",
      dataIndex: "activo",
      key: "activo",
      width: 80,
      align: "center" as const,
      render: (activo: boolean, record: ConfigOption) => (
        <Switch
          size="small"
          checked={activo}
          onChange={() => handleToggleActivo(record)}
        />
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 100,
      align: "center" as const,
      render: (_: any, record: ConfigOption) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              form.setFieldsValue({
                valor: record.valor,
                descripcion: record.descripcion,
              });
              setModalOpen(true);
            }}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleHardDelete(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <strong style={{ whiteSpace: "nowrap" }}>{label}</strong>
        <div style={{ flex: 1 }} />
        <Input
          placeholder={`Buscar en ${label.toLowerCase()}...`}
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          allowClear
          size="middle"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Agregar
        </Button>
      </div>
      <Table
        dataSource={filteredOptions}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        style={{ marginBottom: 24 }}
      />
      <Modal
        open={modalOpen}
        title={editing ? "Editar opción" : "Nueva opción"}
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="valor"
            label="Valor"
            rules={[
              { required: true, message: "Requerido" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const needle = strip(value);
                  const duplicate = options.find(
                    (o) => strip(o.valor) === needle && o.id !== editing?.id,
                  );
                  return duplicate
                    ? Promise.reject(`"${value.trim()}" ya existe`)
                    : Promise.resolve();
                },
              },
            ]}
          >
            {/* onKeyDown stopPropagation: aísla el teclado del input de
                cualquier handler de teclado de componentes ancestros en el
                árbol de React (los portales de AntD propagan eventos
                sintéticos al árbol de componentes, no al DOM). Sin esto, las
                flechas ← →, Inicio/Fin, etc. pueden no mover el cursor al
                editar. Dejamos pasar Escape para que el modal siga cerrándose
                con esa tecla. */}
            <Input
              autoFocus
              onKeyDown={(e) => {
                if (e.key !== "Escape") e.stopPropagation();
              }}
            />
          </Form.Item>
          {showDescripcion && (
            <Form.Item name="descripcion" label="Descripción">
              <Input.TextArea
                rows={2}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") e.stopPropagation();
                }}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}

export default function ConfigTab() {
  const handleSeed = async () => {
    const res = await configOptionApi.seed();
    if (res?.seeded) {
      getMsg()?.success("Opciones iniciales creadas");
      window.location.reload();
    } else if (res) {
      getMsg()?.info("Las opciones ya están inicializadas");
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <Popconfirm
          title="¿Cargar opciones por defecto?"
          onConfirm={handleSeed}
        >
          <Button size="small">Inicializar valores por defecto</Button>
        </Popconfirm>
      </div>
      {CATEGORIAS.map((c) => (
        <CategorySection
          key={c.key}
          categoria={c.key}
          label={c.label}
          clavePersistencia={`configuracion.opciones.${c.key}`}
        />
      ))}
    </div>
  );
}
